// OnboardingFlow — overlay full-screen 5 étapes (handoff Claude Design,
// sa-views2.jsx → OnboardingFlow). Brief V2 §3.7C : "premier insight en
// < 10 min". Le step 0 "Compte" du design est ignoré : ici l'user est déjà
// authentifié quand le flow démarre.
//
// Trigger via event global `sa-onboarding:open` (pattern aligné sur
// ConsentBanner). Le replay depuis la sidebar (chrome) ou le banner d'invite
// BriefHome dispatch l'event.
//
// Étapes :
//   1. URL site                : input + POST /onboarding/analyze
//   2. Profil détecté          : confirme/corrige → POST /onboarding/profile
//   3. Connecter 1re source    : lien /connectors ou skip
//   4. Loading                 : progression RÉELLE — score puis insights
//                                (cahier §3 Lot 0 : pas de faux loading)
//   5. Premier wow             : ScoreRing + 3 insights réels → ferme l'overlay

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import ScoreRing from '@/components/charts/ScoreRing'
import ActivationErrorState from '@/components/connectors/ActivationErrorState'
import { apiFetch, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { type StringKey, useT } from '@/lib/i18n'
import { track } from '@/lib/tracking'

export const ONBOARDING_OPEN_EVENT = 'sa-onboarding:open'

// Clé sessionStorage pour persister l'état du flow à travers les rechargements
// de page. L'OAuth d'un connecteur navigue hors de l'app (window.location.href),
// donc sans persistance le user revient à la step 1, perte sèche d'UX.
const STORAGE_KEY = 'sa-onboarding:state-v1'

type StepNumber = 1 | 2 | 3 | 4 | 5 | 6
const TOTAL_STEPS = 6

type PersistedState = {
  step: StepNumber
  url: string
  detected: DetectedProfile | null
  fallback: boolean
}

function loadPersisted(): PersistedState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PersistedState>
    if (!parsed || !parsed.step || parsed.step < 1 || parsed.step > TOTAL_STEPS) return null
    return {
      step: parsed.step as StepNumber,
      url: typeof parsed.url === 'string' ? parsed.url : 'https://',
      detected: parsed.detected ?? null,
      fallback: !!parsed.fallback,
    }
  } catch {
    return null
  }
}

function clearPersisted(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // sessionStorage indispo (mode privé strict) — pas grave.
  }
}

export function openOnboarding(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(ONBOARDING_OPEN_EVENT))
}

type DetectedProfile = {
  url: string
  sector: string
  market: string
  brand_keywords: string[]
  description: string
  detected_tools: Record<string, boolean>
  confidence_score: number | null
}

type AnalyzeResponse =
  | { profile: DetectedProfile; fallback: false }
  | { profile: null; fallback: true; reason: string }

type HealthScore = { score: number; delta: number | null; has_data: boolean }

type Insight = {
  id: string
  title: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  summary: string
  actions?: Array<{ title: string }>
}

// Progression réelle du step 4 (cahier §3 Lot 0 : pas de faux loading).
// Chaque phase correspond à un appel API observable, pas à un timer.
type PhaseStatus = 'pending' | 'active' | 'done' | 'error'
type LoadingPhases = {
  connection: PhaseStatus
  score: PhaseStatus
  insights: PhaseStatus
}

// Au-delà de ce seuil, on affiche un message rassurant ("plus long que
// d'habitude") plutôt que de laisser le user dans le silence. Pas un timeout
// dur : le flow continue, c'est juste un signal honnête.
const SLOW_THRESHOLD_MS = 30_000

export default function OnboardingFlow() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    function onOpen() {
      setOpen(true)
    }
    window.addEventListener(ONBOARDING_OPEN_EVENT, onOpen)
    // Auto-open via query param `?onboarding=1` — utile pour dev, replay
    // depuis Settings, ou deep-link depuis emails (welcome).
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.get('onboarding') === '1') {
        setOpen(true)
        params.delete('onboarding')
        const newSearch = params.toString()
        const newUrl =
          window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash
        window.history.replaceState({}, '', newUrl)
      } else if (loadPersisted()) {
        // Flow démarré, user parti faire son OAuth, retour sur l'app :
        // on rouvre automatiquement à l'étape où il s'était arrêté.
        setOpen(true)
      }
    }
    return () => window.removeEventListener(ONBOARDING_OPEN_EVENT, onOpen)
  }, [])
  if (!open) return null
  return (
    <OnboardingShell
      onClose={(opts) => {
        // keepState=true : on ferme l'overlay (ex. user part faire l'OAuth)
        // mais on garde le sessionStorage pour pouvoir reprendre au retour.
        if (!opts?.keepState) clearPersisted()
        setOpen(false)
      }}
    />
  )
}

function OnboardingShell({ onClose }: { onClose: (opts?: { keepState?: boolean }) => void }) {
  const t = useT()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { state } = useAuth()
  const workspace = state.workspaces[0]
  // Hydrate depuis sessionStorage si on reprend un flow en cours (cas OAuth :
  // user redirigé hors app puis ramené, React state perdu).
  const persisted = useMemo(() => loadPersisted(), [])
  const [step, setStep] = useState<StepNumber>(persisted?.step ?? 1)
  const [url, setUrl] = useState(persisted?.url ?? 'https://')
  const [detected, setDetected] = useState<DetectedProfile | null>(persisted?.detected ?? null)
  const [fallback, setFallback] = useState<boolean>(persisted?.fallback ?? false)
  const [analyzing, setAnalyzing] = useState(false)
  const [analyzeError, setAnalyzeError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [quizSaving, setQuizSaving] = useState(false)
  const [phases, setPhases] = useState<LoadingPhases>({
    connection: 'pending',
    score: 'pending',
    insights: 'pending',
  })
  const [slowSignal, setSlowSignal] = useState(false)
  const [finalScore, setFinalScore] = useState<HealthScore | null>(null)
  const [finalInsights, setFinalInsights] = useState<Insight[]>([])
  const [loadWowError, setLoadWowError] = useState<string | null>(null)
  // Mesure latence step 4 → first_insight_shown (cahier §6 measurement plan).
  const step4StartedAt = useRef<number | null>(null)
  // Garde-fou : tracker `first_insight_shown` une seule fois par flow,
  // même si la step 5 re-render.
  const firstInsightTracked = useRef(false)

  // Event onboarding_step_viewed à chaque changement de step (cahier §6).
  useEffect(() => {
    track('onboarding_step_viewed', { step })
  }, [step])

  // Event first_insight_shown à l'arrivée step 6 si on a effectivement un
  // insight à montrer (sinon l'event mentirait sur le vrai "premier wow").
  useEffect(() => {
    if (step !== 6) return
    if (firstInsightTracked.current) return
    if (finalInsights.length === 0) return
    const latencyMs =
      step4StartedAt.current !== null
        ? Math.round(performance.now() - step4StartedAt.current)
        : null
    track('first_insight_shown', { latency_ms: latencyMs, count: finalInsights.length })
    firstInsightTracked.current = true
  }, [step, finalInsights])

  // Persiste le state critique à chaque changement utile (step / URL /
  // profil détecté / flag fallback). Step 5 n'est pas persisté : si le user
  // a atteint le wow, c'est terminé, pas besoin de rouvrir au F5.
  useEffect(() => {
    if (step >= TOTAL_STEPS) return
    const payload: PersistedState = { step, url, detected, fallback }
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // sessionStorage indispo (mode privé strict) — pas grave, on continue
      // sans persistance.
    }
  }, [step, url, detected, fallback])

  // Loading step 4 — progression RÉELLE. À l'entrée du step 4, l'OAuth est
  // déjà passé (sinon l'user serait toujours en step 3 ou aurait skippé) :
  // on coche "connexion" immédiatement, puis on enchaîne les vrais appels
  // score → insights. Aucune barre simulée, aucun timer cosmétique.
  useEffect(() => {
    if (step !== 5) return
    setPhases({ connection: 'done', score: 'pending', insights: 'pending' })
    setSlowSignal(false)
    step4StartedAt.current = performance.now()
    // Garde-fou honnête : au-delà de ce seuil, on signale au user que c'est
    // anormalement long, sans interrompre le flow.
    const slowTimer = window.setTimeout(() => setSlowSignal(true), SLOW_THRESHOLD_MS)
    void loadFinalWow()
    return () => window.clearTimeout(slowTimer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  async function loadFinalWow() {
    if (!workspace?.id) {
      // Cas rare mais bloquant : l'auth n'a pas encore résolu le workspace
      // (ex. retour OAuth dans une nouvelle session, refresh raté). On
      // affiche step 5 quand même, mais avec un message explicite plutôt
      // qu'un écran blanc.
      setLoadWowError(t('onboarding.s6.errorNoWorkspace'))
      setStep(6)
      return
    }
    // Phase score — best-effort : si l'API n'a rien, on garde finalScore=null
    // et la step 5 affiche le placeholder "no data". On marque la phase en
    // 'error' uniquement (pas en 'done') pour que la checklist reste honnête.
    setPhases((p) => ({ ...p, score: 'active' }))
    try {
      const score = await apiFetch<HealthScore>(`/api/v1/health-score?workspaceId=${workspace.id}`)
      setFinalScore(score)
      setPhases((p) => ({ ...p, score: 'done' }))
    } catch {
      setFinalScore(null)
      setPhases((p) => ({ ...p, score: 'error' }))
    }
    // Phase insights — même contrat.
    setPhases((p) => ({ ...p, insights: 'active' }))
    try {
      const ins = await apiFetch<{ insights: Insight[] }>(
        `/api/v1/insights?workspaceId=${workspace.id}&status=open`,
      )
      setFinalInsights((ins.insights ?? []).slice(0, 3))
      setPhases((p) => ({ ...p, insights: 'done' }))
    } catch {
      setFinalInsights([])
      setPhases((p) => ({ ...p, insights: 'error' }))
    }
    // Refresh tout — BriefHome se mettra à jour à la fermeture.
    void queryClient.invalidateQueries()
    setStep(6)
  }

  async function handleAnalyze() {
    const cleaned = url.trim()
    if (!/^https?:\/\/.+\..+/.test(cleaned)) {
      setAnalyzeError(t('onboarding.error.url'))
      return
    }
    setAnalyzeError(null)
    setAnalyzing(true)
    try {
      const res = await apiFetch<AnalyzeResponse>('/api/v1/onboarding/analyze', {
        method: 'POST',
        body: { url: cleaned },
      })
      track('url_submitted', { fallback: res.fallback })
      if (res.fallback) {
        // Fallback : on saute l'écran "profil détecté" et on va direct à la
        // connexion. On flag explicitement le mode dégradé pour que la
        // step 3 affiche un chip "mode manuel" et ne laisse pas l'user
        // se demander pourquoi son profil n'a pas été détecté.
        setDetected({
          url: cleaned,
          sector: '',
          market: '',
          brand_keywords: [],
          description: '',
          detected_tools: {},
          confidence_score: null,
        })
        setFallback(true)
        setStep(3)
      } else {
        setDetected(res.profile)
        setFallback(false)
        setStep(2)
      }
    } catch (err) {
      setAnalyzeError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('onboarding.error.generic'),
      )
    } finally {
      setAnalyzing(false)
    }
  }

  async function handleSaveProfile() {
    if (!detected || !workspace?.id) return
    setSaving(true)
    try {
      await apiFetch('/api/v1/onboarding/profile', {
        method: 'POST',
        body: {
          workspaceId: workspace.id,
          url: detected.url,
          sector: detected.sector,
          market: detected.market,
          brand_keywords: detected.brand_keywords,
          description: detected.description,
          detected_tools: detected.detected_tools,
          confidence_score: detected.confidence_score ?? undefined,
        },
      })
      track('profile_confirmed')
      setStep(3) // quiz maturité
    } catch (err) {
      setAnalyzeError(err instanceof Error ? err.message : t('onboarding.error.generic'))
    } finally {
      setSaving(false)
    }
  }

  function handleClose() {
    if (window.confirm(t('onboarding.exit.confirm'))) {
      // L'event onboarding_dropped n'a de sens que si l'user abandonne en
      // cours de flow — pas s'il est déjà arrivé au wow (step 5).
      if (step < TOTAL_STEPS) track('onboarding_dropped', { step })
      onClose()
    }
  }

  function handleConnect() {
    track('connector_connect_started', { source: 'shopify' })
    // L'OAuth d'un connecteur sort de l'app (window.location.href = ...),
    // donc le state React est perdu. On force-sauve l'état actuel en
    // sessionStorage avec step=3 (pour que l'auto-open au retour rouvre
    // sur l'écran connexion plutôt que step 1), puis on ferme l'overlay
    // et on navigue vers /connectors.
    try {
      const payload: PersistedState = { step: 4, url, detected, fallback }
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // sessionStorage indispo : on continue, dans le pire des cas l'user
      // devra rouvrir le flow manuellement depuis Settings.
    }
    onClose({ keepState: true })
    navigate('/connectors')
  }

  return (
    <div
      className="fixed inset-0 z-[2000] flex flex-col bg-bg-0"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboarding.flowTitle')}
    >
      <TopBar step={step} onClose={handleClose} />

      <div className="flex flex-1 items-center justify-center overflow-y-auto px-6 py-10">
        <div key={step} className="w-full max-w-[620px] animate-[fadeUp_.28s_ease]">
          {step === 1 && (
            <StepUrl
              url={url}
              onUrlChange={setUrl}
              analyzing={analyzing}
              error={analyzeError}
              onSubmit={handleAnalyze}
            />
          )}
          {step === 2 && detected && (
            <StepProfile
              profile={detected}
              onCorrect={() => {
                track('profile_corrected')
                setStep(1)
              }}
              onConfirm={handleSaveProfile}
              saving={saving}
              error={analyzeError}
            />
          )}
          {step === 3 && (
            <StepQuiz
              detectedTools={detected?.detected_tools}
              saving={quizSaving}
              onSubmit={async (quiz) => {
                if (!workspace?.id) return
                setQuizSaving(true)
                try {
                  await apiFetch('/api/v1/onboarding/profile', {
                    method: 'POST',
                    body: {
                      workspaceId: workspace.id,
                      url: detected?.url || url,
                      sector: detected?.sector || '',
                      market: detected?.market || '',
                      ...quiz,
                    },
                  })
                  track('maturity_quiz_completed', {
                    business_model: quiz.business_model,
                    maturity_level: quiz.maturity_level,
                    primary_goal: quiz.primary_goal,
                    stack_count: quiz.current_stack.length,
                  })
                  setStep(4)
                } catch (err) {
                  setAnalyzeError(
                    err instanceof Error ? err.message : t('onboarding.error.generic'),
                  )
                } finally {
                  setQuizSaving(false)
                }
              }}
            />
          )}
          {step === 4 && (
            <StepConnect onConnect={handleConnect} onSkip={() => setStep(5)} fallback={fallback} />
          )}
          {step === 5 && <StepLoading phases={phases} slow={slowSignal} />}
          {step === 6 && (
            <StepWow
              score={finalScore}
              insights={finalInsights}
              firstName={(state.user?.full_name ?? '').split(' ')[0] || ''}
              loadError={loadWowError}
              onDone={() => {
                track('onboarding_completed', {
                  has_score: finalScore?.has_data ?? false,
                  insights_count: finalInsights.length,
                })
                if (workspace?.id) {
                  void apiFetch('/api/v1/onboarding/complete', {
                    method: 'POST',
                    body: { workspaceId: workspace.id },
                  })
                }
                onClose()
                navigate('/')
              }}
            />
          )}
        </div>
      </div>

      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  )
}

// ─── Top bar : logo + progress + close ───────────────────────────────────

function TopBar({ step, onClose }: { step: number; onClose: () => void }) {
  const t = useT()
  return (
    <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-7 py-4">
      <div className="flex items-center gap-2.5">
        <img
          src="/brand/ascent-appicon-512.png"
          alt=""
          width={28}
          height={28}
          style={{ borderRadius: 8 }}
          className="flex-shrink-0"
        />
        <span className="font-head text-[15px] font-bold tracking-[-0.02em] text-text-1">
          Smart<span className="font-semibold text-text-2">Analyst</span>
        </span>
      </div>
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: TOTAL_STEPS }, (_, k) => k + 1).map((i) => (
            <div
              key={i}
              className={[
                'h-[7px] rounded-[4px] transition-all duration-300',
                i === step ? 'w-[26px]' : 'w-[7px]',
                i <= step ? 'bg-brand-blue-deep' : 'bg-bg-3',
              ].join(' ')}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="font-mono text-xs text-text-3 transition-colors hover:text-text-2"
        >
          {t('onboarding.exit')} ✕
        </button>
      </div>
    </div>
  )
}

// ─── Step 1 : URL ────────────────────────────────────────────────────────

function StepUrl({
  url,
  onUrlChange,
  analyzing,
  error,
  onSubmit,
}: {
  url: string
  onUrlChange: (v: string) => void
  analyzing: boolean
  error: string | null
  onSubmit: () => void
}) {
  const t = useT()
  return (
    <>
      <Eyebrow n={1} hint={t('onboarding.s1.hint')} />
      <h1 className="mb-2.5 font-head text-[26px] font-bold tracking-[-0.02em] text-text-1">
        {t('onboarding.s1.title')}
      </h1>
      <p className="mb-6 text-[14.5px] leading-[1.6] text-text-2">{t('onboarding.s1.body')}</p>
      <div className="sa-card flex items-center gap-2.5">
        <span className="font-mono text-sm text-text-3">https://</span>
        <input
          type="url"
          value={url.replace(/^https?:\/\//, '')}
          onChange={(e) => onUrlChange('https://' + e.target.value)}
          placeholder="atelier-lumi.fr"
          className="flex-1 border-none bg-transparent py-2 text-base text-text-1 outline-none placeholder:text-text-3"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !analyzing) onSubmit()
          }}
        />
      </div>
      {error && (
        <div className="mt-3 rounded-[10px] border border-brand-red/30 bg-brand-red/10 px-3 py-2 text-sm text-brand-red">
          {error}
        </div>
      )}
      <button
        type="button"
        onClick={onSubmit}
        disabled={analyzing}
        className="sa-btn sa-btn-primary mt-4 w-full !py-3.5 !text-base disabled:opacity-60"
      >
        {analyzing ? t('onboarding.s1.analyzing') : t('onboarding.s1.cta')} →
      </button>
    </>
  )
}

// ─── Step 2 : Profil détecté ─────────────────────────────────────────────

function StepProfile({
  profile,
  onCorrect,
  onConfirm,
  saving,
  error,
}: {
  profile: DetectedProfile
  onCorrect: () => void
  onConfirm: () => void
  saving: boolean
  error: string | null
}) {
  const t = useT()
  const tools = useMemo(
    () =>
      Object.entries(profile.detected_tools || {})
        .filter(([, v]) => v)
        .map(([k]) => k),
    [profile.detected_tools],
  )
  return (
    <>
      <Eyebrow n={2} hint={t('onboarding.s2.hint')} />
      <div className="mb-5 flex items-center gap-4">
        <ProfileIllus />
        <div>
          <h1 className="mb-1.5 font-head text-[25px] font-bold tracking-[-0.02em] text-text-1">
            {t('onboarding.s2.title')} 👌
          </h1>
          <p className="text-[14px] leading-[1.55] text-text-2">
            {t('onboarding.s2.body')}{' '}
            <strong className="text-text-1">{profile.url.replace(/^https?:\/\//, '')}</strong>.
          </p>
        </div>
      </div>

      <div className="sa-card !p-0">
        {profile.description && (
          <Row label={t('onboarding.s2.activity')}>{profile.description}</Row>
        )}
        {profile.sector && <Row label={t('onboarding.s2.sector')}>{profile.sector}</Row>}
        {profile.market && <Row label={t('onboarding.s2.market')}>{profile.market}</Row>}
        {tools.length > 0 && (
          <Row label={t('onboarding.s2.tools')}>
            <div className="flex flex-wrap gap-1.5">
              {tools.map((tool) => (
                <span key={tool} className="sa-chip bg-brand-blue-dim text-brand-blue-deep">
                  {tool}
                </span>
              ))}
            </div>
          </Row>
        )}
        {profile.brand_keywords?.length > 0 && (
          <Row label={t('onboarding.s2.keywords')} last>
            <div className="flex flex-wrap gap-1.5">
              {profile.brand_keywords.map((k) => (
                <span key={k} className="sa-chip bg-brand-cyan/10 text-brand-cyan">
                  {k}
                </span>
              ))}
            </div>
          </Row>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-[10px] border border-brand-red/30 bg-brand-red/10 px-3 py-2 text-sm text-brand-red">
          {error}
        </div>
      )}

      <div className="mt-4 flex gap-2.5">
        <button type="button" onClick={onCorrect} className="sa-btn !text-sm">
          {t('onboarding.s2.correct')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={saving}
          className="sa-btn sa-btn-primary flex-1 !text-sm disabled:opacity-60"
        >
          {saving ? t('onboarding.s2.saving') : t('onboarding.s2.confirm')} →
        </button>
      </div>
    </>
  )
}

function Row({
  label,
  children,
  last,
}: {
  label: string
  children: React.ReactNode
  last?: boolean
}) {
  return (
    <div
      className={['flex items-start gap-4 px-5 py-3.5', last ? '' : 'border-b border-border'].join(
        ' ',
      )}
    >
      <span className="w-20 flex-shrink-0 pt-1 font-mono text-[11.5px] uppercase tracking-[0.04em] text-text-3">
        {label}
      </span>
      <div className="flex-1 text-[14.5px] font-medium leading-[1.5] text-text-1">{children}</div>
    </div>
  )
}

// ─── Step 3 : Quiz maturité ──────────────────────────────────────────────

const BUSINESS_MODELS = ['b2b', 'b2c', 'b2b2c', 'marketplace', 'other'] as const
const MATURITY_LEVELS = ['beginner', 'intermediate', 'advanced'] as const
const PRIMARY_GOALS = [
  'grow_revenue',
  'reduce_cac',
  'improve_retention',
  'optimize_campaigns',
  'understand_customers',
] as const
const STACK_OPTIONS = [
  'GA4',
  'Google Ads',
  'Meta Ads',
  'Shopify',
  'WooCommerce',
  'Stripe',
  'HubSpot',
  'Mailchimp',
  'Klaviyo',
  'Search Console',
  'TikTok Ads',
  'LinkedIn Ads',
  'Semrush',
  'Ahrefs',
] as const

type QuizData = {
  business_model: string
  maturity_level: string
  primary_goal: string
  current_stack: string[]
}

function StepQuiz({
  detectedTools,
  saving,
  onSubmit,
}: {
  detectedTools?: Record<string, boolean>
  saving: boolean
  onSubmit: (data: QuizData) => void
}) {
  const t = useT()
  const [businessModel, setBusinessModel] = useState('')
  const [maturityLevel, setMaturityLevel] = useState('')
  const [primaryGoal, setPrimaryGoal] = useState('')
  const [stack, setStack] = useState<string[]>(() => {
    if (!detectedTools) return []
    return Object.entries(detectedTools)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .filter((k) => (STACK_OPTIONS as readonly string[]).includes(k))
  })
  const canSubmit = businessModel && maturityLevel && primaryGoal

  function toggleStack(tool: string) {
    setStack((prev) => (prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]))
  }

  return (
    <>
      <Eyebrow n={3} hint={t('onboarding.s3.hint')} />
      <h1 className="mb-2.5 font-head text-[26px] font-bold tracking-[-0.02em] text-text-1">
        {t('onboarding.s3.title')}
      </h1>
      <p className="mb-6 text-[14.5px] leading-[1.6] text-text-2">{t('onboarding.s3.body')}</p>

      {/* Business model */}
      <fieldset className="mb-5">
        <legend className="sa-label mb-2">{t('onboarding.s3.businessModel')}</legend>
        <div className="flex flex-wrap gap-2">
          {BUSINESS_MODELS.map((bm) => (
            <button
              key={bm}
              type="button"
              onClick={() => setBusinessModel(bm)}
              className={[
                'sa-chip cursor-pointer transition-colors',
                businessModel === bm
                  ? 'bg-brand-blue-deep text-white'
                  : 'bg-bg-2 text-text-2 hover:bg-bg-3',
              ].join(' ')}
            >
              {t(`onboarding.s3.businessModel.${bm}` as StringKey)}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Maturity level */}
      <fieldset className="mb-5">
        <legend className="sa-label mb-2">{t('onboarding.s3.maturityLevel')}</legend>
        <div className="flex flex-col gap-2">
          {MATURITY_LEVELS.map((ml) => (
            <button
              key={ml}
              type="button"
              onClick={() => setMaturityLevel(ml)}
              className={[
                'flex items-center gap-3 rounded-[10px] border px-4 py-3 text-left transition-colors',
                maturityLevel === ml
                  ? 'border-brand-blue-deep bg-brand-blue-deep/5'
                  : 'border-border bg-bg-1 hover:border-border-bright',
              ].join(' ')}
            >
              <span
                className={[
                  'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2',
                  maturityLevel === ml ? 'border-brand-blue-deep' : 'border-border',
                ].join(' ')}
              >
                {maturityLevel === ml && (
                  <span className="h-2.5 w-2.5 rounded-full bg-brand-blue-deep" />
                )}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-text-1">
                  {t(`onboarding.s3.maturityLevel.${ml}` as StringKey)}
                </div>
                <div className="text-[12.5px] text-text-2">
                  {t(`onboarding.s3.maturityLevel.${ml}.desc` as StringKey)}
                </div>
              </div>
            </button>
          ))}
        </div>
      </fieldset>

      {/* Primary goal */}
      <fieldset className="mb-5">
        <legend className="sa-label mb-2">{t('onboarding.s3.primaryGoal')}</legend>
        <div className="flex flex-wrap gap-2">
          {PRIMARY_GOALS.map((pg) => (
            <button
              key={pg}
              type="button"
              onClick={() => setPrimaryGoal(pg)}
              className={[
                'sa-chip cursor-pointer transition-colors',
                primaryGoal === pg
                  ? 'bg-brand-blue-deep text-white'
                  : 'bg-bg-2 text-text-2 hover:bg-bg-3',
              ].join(' ')}
            >
              {t(`onboarding.s3.primaryGoal.${pg}` as StringKey)}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Current stack */}
      <fieldset className="mb-6">
        <legend className="sa-label mb-2">{t('onboarding.s3.currentStack')}</legend>
        <div className="flex flex-wrap gap-2">
          {STACK_OPTIONS.map((tool) => (
            <button
              key={tool}
              type="button"
              onClick={() => toggleStack(tool)}
              className={[
                'sa-chip cursor-pointer transition-colors',
                stack.includes(tool)
                  ? 'bg-brand-cyan/15 text-brand-cyan'
                  : 'bg-bg-2 text-text-2 hover:bg-bg-3',
              ].join(' ')}
            >
              {tool}
            </button>
          ))}
        </div>
      </fieldset>

      <button
        type="button"
        disabled={!canSubmit || saving}
        onClick={() =>
          onSubmit({
            business_model: businessModel,
            maturity_level: maturityLevel,
            primary_goal: primaryGoal,
            current_stack: stack,
          })
        }
        className="sa-btn sa-btn-primary w-full !py-3.5 !text-base disabled:opacity-60"
      >
        {saving ? t('onboarding.s3.saving') : t('onboarding.s3.cta')} →
      </button>
    </>
  )
}

// ─── Step 4 : Connecter 1re source ───────────────────────────────────────

function StepConnect({
  onConnect,
  onSkip,
  fallback,
}: {
  onConnect: () => void
  onSkip: () => void
  fallback: boolean
}) {
  const t = useT()
  return (
    <>
      <Eyebrow n={4} hint={t('onboarding.s4.hint')} />
      <h1 className="mb-2.5 font-head text-[25px] font-bold tracking-[-0.02em] text-text-1">
        {t('onboarding.s4.title')}
      </h1>
      <p className="mb-3 text-[14.5px] leading-[1.6] text-text-2">{t('onboarding.s4.body')}</p>
      {fallback && (
        <div className="mb-5 flex items-start gap-2.5 rounded-[10px] border border-brand-amber/30 bg-brand-amber/10 px-3.5 py-2.5 text-[13px] text-text-1">
          <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-brand-amber font-bold text-white">
            !
          </span>
          <span>{t('onboarding.s4.fallback')}</span>
        </div>
      )}
      <div className="sa-card">
        <div className="mb-4 flex items-center gap-3.5">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[13px] bg-[#5E8E3E]">
            <svg width="28" height="28" viewBox="0 0 48 48" aria-hidden="true">
              <rect x="16" y="19" width="16" height="15" rx="3" fill="#fff" />
              <path d="M19 20a5 5 0 0 1 10 0" fill="none" stroke="#fff" strokeWidth="2.4" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="font-head text-base font-bold text-text-1">Shopify</div>
            <div className="text-[13px] text-text-2">{t('onboarding.s4.desc')}</div>
          </div>
          <span className="sa-chip bg-brand-cyan/10 text-brand-cyan">
            {t('onboarding.s4.recommended')}
          </span>
        </div>
        <button type="button" onClick={onConnect} className="sa-btn sa-btn-primary w-full !text-sm">
          {t('onboarding.s4.connect')}
        </button>
      </div>
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onSkip}
          className="text-[13px] text-text-3 hover:text-text-2"
        >
          {t('onboarding.s4.later')} →
        </button>
      </div>
    </>
  )
}

// ─── Step 5 : Loading ────────────────────────────────────────────────────

function StepLoading({ phases, slow }: { phases: LoadingPhases; slow: boolean }) {
  const t = useT()
  const checks: Array<{ key: keyof LoadingPhases; label: string }> = [
    { key: 'connection', label: t('onboarding.s5.check1') },
    { key: 'score', label: t('onboarding.s5.check2') },
    { key: 'insights', label: t('onboarding.s5.check3') },
  ]
  const hasError = phases.score === 'error' || phases.insights === 'error'
  return (
    <div className="text-center">
      <ConnectIllus />
      <h1 className="mb-2.5 mt-6 font-head text-[23px] font-bold tracking-[-0.02em] text-text-1">
        {t('onboarding.s5.title')}
      </h1>
      <p className="mb-6 text-[14px] text-text-2">{t('onboarding.s5.body')}</p>
      <div
        className="mx-auto mb-6 flex items-center justify-center"
        role="status"
        aria-label={t('onboarding.s5.live')}
      >
        <Spinner />
      </div>
      <div className="flex flex-col gap-2 text-left" aria-live="polite" aria-atomic="false">
        {checks.map(({ key, label }) => {
          const status = phases[key]
          return (
            <div
              key={key}
              className={[
                'flex items-center gap-2.5 transition-opacity duration-300',
                status === 'pending' ? 'opacity-40' : 'opacity-100',
              ].join(' ')}
            >
              <PhaseDot status={status} />
              <span className="text-[13px] text-text-2">{label}</span>
            </div>
          )
        })}
      </div>
      {slow && !hasError && (
        <div
          className="mx-auto mt-5 max-w-[440px] rounded-[10px] border border-brand-amber/30 bg-brand-amber/10 px-3.5 py-2.5 text-left text-[13px] text-text-1"
          role="status"
        >
          {t('onboarding.s5.slow')}
        </div>
      )}
      {hasError && (
        <div
          className="mx-auto mt-5 max-w-[440px] rounded-[10px] border border-brand-amber/30 bg-brand-amber/10 px-3.5 py-2.5 text-left text-[13px] text-text-1"
          role="status"
        >
          {t('onboarding.s5.error')}
        </div>
      )}
    </div>
  )
}

function Spinner() {
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true" className="animate-spin">
      <circle
        cx="17"
        cy="17"
        r="13"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeDasharray="60 20"
        className="text-brand-blue-deep"
      />
    </svg>
  )
}

function PhaseDot({ status }: { status: PhaseStatus }) {
  if (status === 'active') {
    return (
      <span
        className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-brand-blue-deep/15 text-brand-blue-deep"
        aria-label="active"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="animate-spin">
          <circle
            cx="5"
            cy="5"
            r="3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="14 8"
          />
        </svg>
      </span>
    )
  }
  if (status === 'done') {
    return (
      <span
        className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-brand-green text-[11px] text-white"
        aria-label="done"
      >
        ✓
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span
        className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-brand-amber text-[11px] text-white"
        aria-label="error"
      >
        !
      </span>
    )
  }
  return (
    <span
      className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-bg-3"
      aria-label="pending"
    />
  )
}

// ─── Step 6 : First wow ──────────────────────────────────────────────────

function StepWow({
  score,
  insights,
  firstName,
  loadError,
  onDone,
}: {
  score: HealthScore | null
  insights: Insight[]
  firstName: string
  loadError: string | null
  onDone: () => void
}) {
  const t = useT()
  const hasScore = score?.has_data && typeof score.score === 'number'
  const insufficient = !loadError && !hasScore && insights.length === 0
  return (
    <>
      <div className="mb-6 text-center">
        <span className="sa-chip mb-3.5 inline-flex bg-brand-green/10 text-brand-green">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
          {t('onboarding.s6.done')}
        </span>
        <h1 className="mb-2.5 font-head text-[27px] font-extrabold tracking-[-0.03em] text-text-1">
          {t('onboarding.s6.title')}
        </h1>
        <p className="text-[14.5px] leading-[1.6] text-text-2">{t('onboarding.s6.body')}</p>
      </div>

      {loadError && (
        <div className="mx-auto max-w-[440px]">
          <ActivationErrorState kind="workspace_not_ready" />
        </div>
      )}
      {insufficient && (
        <div className="mx-auto max-w-[440px]">
          <ActivationErrorState kind="insufficient_data" />
        </div>
      )}

      <div className="sa-card mb-3.5">
        <div className="flex items-center gap-6">
          {hasScore ? (
            <ScoreRing
              value={score!.score}
              size={116}
              label={t('brief.score.label')}
              delta={score!.delta}
            />
          ) : (
            <div className="flex h-[116px] w-[116px] flex-shrink-0 items-center justify-center rounded-full border-2 border-dashed border-border text-text-3">
              <span className="font-mono text-xs">—</span>
            </div>
          )}
          <div className="flex-1">
            <div className="mb-1.5 font-head text-[17px] font-bold text-text-1">
              {firstName
                ? t('onboarding.s6.cardTitleNamed', { name: firstName })
                : t('onboarding.s6.cardTitle')}
            </div>
            <p className="text-[13.5px] leading-[1.55] text-text-2">
              {hasScore ? t('onboarding.s6.cardBody') : t('onboarding.s6.cardBodyNoData')}
            </p>
          </div>
        </div>
      </div>

      {insights.length > 0 && (
        <div className="mb-5 flex flex-col gap-2.5">
          {insights.map((i) => (
            <div
              key={i.id}
              className="flex items-start gap-3 rounded-brief border border-border bg-card p-3.5 shadow-card"
            >
              <SevDot sev={i.severity} />
              <div className="min-w-0 flex-1">
                <div className="mb-0.5 truncate text-sm font-semibold text-text-1">{i.title}</div>
                <div className="text-[12.5px] leading-[1.45] text-text-2 line-clamp-2">
                  {i.actions?.[0]?.title ?? i.summary}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onDone}
        className="sa-btn sa-btn-primary w-full !py-3.5 !text-base"
      >
        {t('onboarding.s6.cta')} →
      </button>
    </>
  )
}

function SevDot({ sev }: { sev: Insight['severity'] }) {
  const cls =
    sev === 'critical' || sev === 'high'
      ? 'bg-brand-red/13 text-brand-red'
      : sev === 'medium'
        ? 'bg-brand-amber/13 text-brand-amber'
        : 'bg-brand-blue-deep/13 text-brand-blue-deep'
  return (
    <span
      className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[9px] font-head text-xs font-extrabold ${cls}`}
    >
      {sev === 'critical' || sev === 'high' ? '!' : sev === 'medium' ? '!' : '↗'}
    </span>
  )
}

// ─── Bits ────────────────────────────────────────────────────────────────

function Eyebrow({ n, hint }: { n: number; hint: string }) {
  const t = useT()
  return (
    <div className="mb-3.5 flex items-center gap-2.5">
      <span className="font-mono text-[11px] tracking-[0.1em] text-brand-blue-deep">
        {t('onboarding.step')} {n}/{TOTAL_STEPS}
      </span>
      <span className="h-px flex-1 bg-border" />
      <span className="font-mono text-[11px] text-text-3">{hint}</span>
    </div>
  )
}

function ProfileIllus() {
  return (
    <svg width="70" height="70" viewBox="0 0 70 70" aria-hidden="true" className="flex-shrink-0">
      <defs>
        <linearGradient id="prof-grad" x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#5C8FFF" />
          <stop offset="1" stopColor="#2DD9EE" />
        </linearGradient>
      </defs>
      <rect
        x="14"
        y="20"
        width="42"
        height="38"
        rx="8"
        fill="none"
        stroke="url(#prof-grad)"
        strokeWidth="2.8"
      />
      <line
        x1="22"
        y1="32"
        x2="42"
        y2="32"
        stroke="#5C5C78"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <line
        x1="22"
        y1="40"
        x2="48"
        y2="40"
        stroke="#5C5C78"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <line
        x1="22"
        y1="48"
        x2="38"
        y2="48"
        stroke="#5C5C78"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="48" cy="48" r="3.5" fill="#2DD9EE" />
    </svg>
  )
}

function ConnectIllus() {
  return (
    <svg width="84" height="84" viewBox="0 0 84 84" className="mx-auto" aria-hidden="true">
      <defs>
        <linearGradient id="conn-grad" x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#5C8FFF" />
          <stop offset="1" stopColor="#2DD9EE" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="42" r="10" fill="none" stroke="url(#conn-grad)" strokeWidth="3" />
      <circle cx="64" cy="42" r="10" fill="none" stroke="#5C5C78" strokeWidth="2.8" />
      <line
        x1="30"
        y1="42"
        x2="54"
        y2="42"
        stroke="url(#conn-grad)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="42" cy="42" r="3.5" fill="#2DD9EE" />
    </svg>
  )
}
